import { Code2, Sparkles, X } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import { cn } from '@/_shared/lib/utils';

/** The controls to the right of a predicate row: a toggle that shows / hides the grounded insight
 *  (`onToggleInsights`), an optional "Show SQL" viewer (`onShowSql`, used by the raw-SQL clause), and a
 *  delete button that removes the whole row (`onRemove`). Each renders only when its input is given.
 *  `disabled` gates the destructive remove (X) only — the insight toggle and the SQL viewer stay live even
 *  on a disabled row, since they only reveal a read-only surface.
 *
 *  Separate buttons, not a joined `ButtonGroup`: the destructive delete sat flush against the toggle people
 *  click most. The gap also gives each button's 2px interaction ring its own room. */
export function PredicateActions({
  insightShown = false,
  onToggleInsights,
  onShowSql,
  onRemove,
  disabled = false,
}: {
  insightShown?: boolean;
  onToggleInsights?: () => void;
  onShowSql?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  if (!onToggleInsights && !onShowSql && !onRemove) return null;
  return (
    <div role="group" aria-label="Row actions" className="flex shrink-0 items-center gap-2">
      {onToggleInsights && (
        <Button
          variant="secondary"
          size="sm"
          iconOnly
          aria-label={insightShown ? 'Hide insights' : 'Show insights'}
          aria-pressed={insightShown}
          onClick={onToggleInsights}
          className={cn(insightShown && 'text-primary')}
          Icon={<Sparkles />}
        />
      )}
      {onShowSql && (
        <Button variant="secondary" size="sm" iconOnly aria-label="Show SQL" onClick={onShowSql} Icon={<Code2 />} />
      )}
      {onRemove && (
        <Button
          variant="secondary"
          size="sm"
          iconOnly
          aria-label="Delete row"
          onClick={onRemove}
          disabled={disabled}
          Icon={<X />}
        />
      )}
    </div>
  );
}
